import type { DriftReport, Finding, Scan, Severity, Tenant, Verdict } from '../api.ts';
import { Sev } from '../Findings.tsx';
import { asDate, asMoment, plural } from './format.ts';
import type { ScheduleView } from './types.ts';

/**
 * The four verdicts, the badge class that draws each one, and what the count
 * beside it means. The glyph and the fill come from `.verdict` in styles.css —
 * this table holds only words, so no two screens can disagree about which
 * character means "not met".
 *
 * Not evidenced is not a weak failure and is never added to the failures. It
 * almost always means the client's licence does not expose the setting, and a
 * screen that shades it towards red tells an MSP their client has a security
 * problem when what they have is a purchasing decision.
 */
const VERDICTS: ReadonlyArray<{
  verdict: Verdict;
  className: string;
  label: string;
  hint: string;
}> = [
  {
    verdict: 'FAIL',
    className: 'verdict-not-met',
    label: 'Not met',
    hint: 'The collected configuration does not satisfy these.',
  },
  {
    verdict: 'INSUFFICIENT_DATA',
    className: 'verdict-not-evidenced',
    label: 'Not evidenced',
    hint: 'Could not be determined — usually a licence gap.',
  },
  {
    verdict: 'PASS',
    className: 'verdict-met',
    label: 'Met',
    hint: 'The collected configuration satisfies these.',
  },
  {
    verdict: 'NOT_APPLICABLE',
    className: 'verdict-not-applicable',
    label: 'Not applicable',
    hint: 'Nothing in this tenant for these to govern.',
  },
];

/** The five drift figures worth a headline, and what each one is not. */
const DRIFT_FIGURES: ReadonlyArray<{ key: string; label: string; hint: string }> = [
  {
    key: 'regressions',
    label: 'Regressed',
    hint: 'Met before, not met now.',
  },
  {
    key: 'improvements',
    label: 'Improved',
    hint: 'Not met before, met now.',
  },
  {
    key: 'evidenceChanged',
    label: 'Changed, still met',
    hint: 'Same verdict, different configuration underneath.',
  },
  {
    key: 'visibilityLost',
    label: 'No longer visible',
    hint: 'We stopped being able to look. Not a regression.',
  },
  {
    key: 'notComparable',
    label: 'Not comparable',
    hint: 'Our own rules or thresholds moved, so the difference may be ours.',
  },
];

const SEVERITY_RANK: Record<Severity, number> = { critical: 0, high: 1, medium: 2, low: 3 };

const CONNECTION: Record<Tenant['connectionState'], { text: string; chip: string }> = {
  connected: { text: 'Connected', chip: 'conn-connected' },
  pending_consent: { text: 'Awaiting consent', chip: 'conn-pending' },
  consent_revoked: { text: 'Consent revoked', chip: 'conn-revoked' },
  insufficient_permissions: { text: 'Insufficient permissions', chip: 'conn-insufficient' },
  error: { text: 'Connection error', chip: 'conn-error' },
};

const countBy = (findings: readonly Finding[], verdict: Verdict): number =>
  findings.filter((finding) => finding.verdict === verdict).length;

const worstFailing = (findings: readonly Finding[]): Severity | null =>
  findings
    .filter((finding) => finding.verdict === 'FAIL')
    .reduce<Severity | null>(
      (worst, finding) =>
        worst === null || SEVERITY_RANK[finding.severity] < SEVERITY_RANK[worst]
          ? finding.severity
          : worst,
      null,
    );

interface OverviewProps {
  tenant: Tenant;
  latest: Scan | null;
  findings: Finding[] | null;
  drift: DriftReport | null;
  driftDetail: string | null;
  schedule: ScheduleView | null;
  /**
   * An assessment is under way for this client — whether this operator started
   * it, another tab did, or the scheduler did. The shell derives it from the
   * scan list; this panel only draws it, and never decides it.
   */
  running: boolean;
  /** What the button says, already resolved to the run's stage by the shell. */
  runLabel: string;
  /**
   * Whether this operator holds `scans.run`. A viewer reads everything and
   * changes nothing, so offering them a button that can only 403 is worse than
   * not offering it — the rest of this panel is theirs to read.
   */
  canRun: boolean;
  onRun: () => void;
  onOpenReport: (scanId: string, print: boolean) => void;
}

export const Overview = ({
  tenant,
  latest,
  findings,
  drift,
  driftDetail,
  schedule,
  running,
  runLabel,
  canRun,
  onRun,
  onOpenReport,
}: OverviewProps) => {
  const connection = CONNECTION[tenant.connectionState];
  const broken =
    tenant.connectionState !== 'connected' && tenant.connectionState !== 'pending_consent';
  const attested = (findings ?? []).filter((finding) => finding.evidenceSource === 'attested');
  const worst = findings === null ? null : worstFailing(findings);
  const unevidenced = findings === null ? 0 : countBy(findings, 'INSUFFICIENT_DATA');

  return (
    <section className="td-stack">
      <div className="card">
        <div className="card-head">
          <h2>Connection</h2>
          <span className={`conn ${connection.chip}`}>{connection.text}</span>
        </div>
        <div className="card-body td-facts">
          <div>
            <span className="label">Microsoft tenant</span>
            <span className="mono">{tenant.entraTenantId}</span>
          </div>
          <div>
            <span className="label">Last assessed</span>
            <span className="num">
              {latest?.completedAt ? asMoment(latest.completedAt) : 'Never'}
            </span>
          </div>
          <div>
            <span className="label">Next scheduled assessment</span>
            <span>{schedule?.decision.detail ?? 'Not in the current sweep plan.'}</span>
          </div>
        </div>
      </div>

      {broken && (
        <div className="callout callout-caution">
          <span className="callout-title">This client cannot be assessed again yet</span>
          {tenant.connectionDetail ??
            'The connection to this tenant has stopped working. Nothing below is refreshing until it is restored.'}{' '}
          The stored assessment below is still a true record of the day it was produced.
        </div>
      )}

      {/*
        Prominent, and deliberately worded. This starts a read-only collection:
        DriftWatch has no write scope in its consent, and nothing on any screen
        in this console may suggest it can change a client's tenant.
      */}
      <div className="card td-run">
        <div className="card-head">
          <h2>Run an assessment</h2>
          {/*
            Disabled for the whole run, not for the HTTP call that starts it.
            The call answers in milliseconds and the collection takes the better
            part of a minute, so a button that re-enabled on the response would
            let a second click queue a second real read of a customer's tenant.
            `aria-busy` draws the spinner; see `.btn[aria-busy]` in styles.css.
          */}
          {canRun && (
            <button
              type="button"
              className="btn btn-primary"
              aria-busy={running || undefined}
              disabled={running}
              onClick={onRun}
            >
              {runLabel}
            </button>
          )}
        </div>
        <div className="card-body">
          <p>
            Reads this client’s Entra configuration, evaluates the control catalogue against it and
            stores a report.
          </p>
          <p className="note td-readonly">
            Read-only. DriftWatch holds no permission to write to a client tenant, so nothing here
            changes a setting — remediation is instructions your engineer follows in the Microsoft
            admin centre.
          </p>
          {running && (
            <p className="note td-running">
              This takes about a minute. The findings below update on their own when it lands —
              there is no need to reload, and no need to run it again.
            </p>
          )}
          {!canRun && (
            <p className="note">
              Running an assessment needs the scans capability, which your role does not carry.
              Everything else on this screen is yours to read.
            </p>
          )}
        </div>
      </div>

      <div className="card">
        <div className="card-head">
          <h2>Latest assessment</h2>
          {latest?.completedAt && (
            <span className="note num">As at {asDate(latest.completedAt)}</span>
          )}
        </div>
        <div className="card-body">
          {latest === null ? (
            <p className="note">
              This client has never been assessed. Run one above; the results appear here.
            </p>
          ) : findings === null ? (
            <p className="note" aria-live="polite">
              Reading the findings of the assessment completed on{' '}
              {latest.completedAt ? asDate(latest.completedAt) : 'this client'}…
            </p>
          ) : (
            <>
              {/* Verdict, then figure, then hint — the same order as the Findings tab's tiles.
                  Those are filter buttons and these are read-only, so they are not the same
                  control; but the same four numbers reordering as you switch tab reads as two
                  different measurements rather than one. */}
              <div className="stat-grid">
                {VERDICTS.map((entry) => (
                  <div className="stat stat-tile" key={entry.verdict}>
                    <span className={`verdict ${entry.className}`}>{entry.label}</span>
                    <span className="stat-figure">{countBy(findings, entry.verdict)}</span>
                    <span className="stat-hint">{entry.hint}</span>
                  </div>
                ))}
              </div>

              <p className="note td-nocount">
                Counts, never a score. DriftWatch derives no percentage from these: a weighted
                figure cannot be defended when an auditor asks how it was arrived at, and it buries
                the controls nobody could evidence inside a denominator (ADR-0007).
              </p>

              {worst !== null && (
                <p className="td-worst">
                  <span className="label">Worst failing severity</span>
                  <Sev severity={worst} />
                </p>
              )}

              {attested.length > 0 && (
                <p className="td-attested">
                  <span className="attested" />
                  <span className="note">
                    {plural(attested.length, 'verdict')} rest partly on an attestation —{' '}
                    {attested.map((finding) => finding.controlId).join(', ')}.
                  </span>
                </p>
              )}

              {unevidenced > 0 && (
                <div className="callout callout-evidence td-gap">
                  <span className="callout-title">
                    {plural(unevidenced, 'control')} could not be assessed
                  </span>
                  {/* Worded exactly as the Findings tab and the rendered report word it. Three
                      phrasings of one fact, one per surface, is how an MSP ends up unsure whether
                      they mean the same thing. */}
                  Usually a licence the tenant has not bought, consent that was revoked, or a
                  question only the organisation can answer. Reported as a gap, never as a security
                  failure, and never counted towards the failures above.
                </div>
              )}

              <div className="toolbar td-actions">
                <button
                  type="button"
                  className="btn btn-quiet"
                  onClick={() => onOpenReport(latest.id, false)}
                >
                  Open the stored report
                </button>
                <button
                  type="button"
                  className="btn btn-quiet"
                  onClick={() => onOpenReport(latest.id, true)}
                >
                  Save as PDF
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="card">
        <div className="card-head">
          <h2>Since the previous assessment</h2>
          {drift !== null && (
            <span className="note num">
              {asDate(drift.from.completedAt)} → {asDate(drift.to.completedAt)}
            </span>
          )}
        </div>
        <div className="card-body">
          {drift === null ? (
            <p className="note">
              {driftDetail ??
                'There is nothing to compare yet — a second completed assessment is needed.'}
            </p>
          ) : drift.identicalSnapshot ? (
            <p>
              Nothing changed. The two collections hashed identically, so this client’s
              configuration is byte-for-byte the same as it was {drift.intervalDays} days ago.
            </p>
          ) : (
            <>
              <div className="stat-grid">
                {DRIFT_FIGURES.map((figure) => (
                  <div className="stat" key={figure.key}>
                    <span className="stat-figure">{drift.summary[figure.key] ?? 0}</span>
                    <span className="stat-label">{figure.label}</span>
                    <span className="stat-hint">{figure.hint}</span>
                  </div>
                ))}
              </div>
              <p className="note td-nocount">
                A verdict moves for three different reasons — the client changed, our visibility
                changed, or we changed — and only the first is a regression. The stored report for
                this assessment sets out every change in full.
              </p>
            </>
          )}
        </div>
      </div>
    </section>
  );
};
